import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { PagingParams } from "../../types/pagination";
import TableFilterBar from "../common/TableFilterBar";
import { all_routes } from "../router/all_routes";
import Paginator from "../common/Paginator";
import TableClient from "../common/TableClient";
import DateCalculator from "./DateCalculator";

const ClientList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);

  const [formData, setFormData] = useState({
    touristVisaIssueDate: "",
    entryDateToUAE: "",
    followUpComments: "",
  });

  const [remainingDaysMap, setRemainingDaysMap] = useState<
    Record<string, {
      remainingDays: number | null;
      allowedDays: number;
      overstayDays: number;
      overstayFine: number;
      phone: string | null;
    }>
  >({});

  const {
    clientStore: {
      loadClientItems,
      clientItems,
      setSearchTerm,
      setPagingParams,
      pagination,
      currentClientItem,
      getClientItem,
      updateVisaDates,
    },
    bookingStore: { calculateRemainingDays, getBooking },
  } = useStore();

  useEffect(() => {
    const fetchBookingAndDays = async () => {
      if (!clientItems || clientItems.length === 0) return;

      const entries = await Promise.all(
        clientItems.map(async (client) => {
          const booking = await getBooking(client.bookingId);
          const duration = parseInt(booking?.duration ?? "", 10);
          const allowedDays = !isNaN(duration) ? duration : 90;
          const phone = booking?.phone ?? null;

          const { remainingDays, overstayDays, overstayFine } = getRemainingDays(client.entryDateToUAE, allowedDays);

          return [
            client.id,
            {
              remainingDays,
              allowedDays,
              overstayFine,
              overstayDays,
              phone,
            },
          ] as [
            string,
            {
              remainingDays: number | null;
              allowedDays: number;
              overstayFine: number;
              overstayDays: number;
              phone: string | null;
            }
          ];
        })
      );

      setRemainingDaysMap(Object.fromEntries(entries));
    };

    fetchBookingAndDays();
  }, [clientItems, getBooking]);

const getRemainingDays = (
  entryDateStr: string | null | undefined,
  allowedDays: number
): {
  remainingDays: number | null;
  overstayFine: number;
  overstayDays: number;
} => {
  if (!entryDateStr) {
    return { remainingDays: null, overstayFine: 0, overstayDays: 0 };
  }

  const entryDate = new Date(entryDateStr);
  if (isNaN(entryDate.getTime())) {
    return { remainingDays: null, overstayFine: 0, overstayDays: 0 };
  }

  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;

  // Strip time from dates (set to midnight)
  entryDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const entryDiffDays = Math.round((entryDate.getTime() - today.getTime()) / msPerDay);

  // If entry is in the future (Not started yet)
  if (entryDiffDays > 0) {
    return {
      remainingDays: allowedDays,
      overstayFine: 0,
      overstayDays: 0,
    };
  }

  // Entry is today or in the past
  const expiryDate = new Date(entryDate);
  expiryDate.setDate(entryDate.getDate() + allowedDays);
  expiryDate.setHours(0, 0, 0, 0);

  const remainingDays = Math.floor((expiryDate.getTime() - today.getTime()) / msPerDay);

  const overstayDays = remainingDays < 0 ? Math.abs(remainingDays) : 0;
  const overstayFine = overstayDays > 0 ? 200 + (overstayDays - 1) * 50 : 0;

  return {
    remainingDays: remainingDays >= 0 ? remainingDays : 0,
    overstayFine,
    overstayDays,
  };
};

  useEffect(() => {
    if (currentClientItem) {
      setFormData({
        touristVisaIssueDate: currentClientItem.touristVisaIssueDate?.split("T")[0] ?? "",
        entryDateToUAE: currentClientItem.entryDateToUAE?.split("T")[0] ?? "",
        followUpComments: "",
      });
    }
  }, [currentClientItem]);

  const getFirstTwoWords = (name: string | null | undefined): string => {
    if (!name) return "";
    return name.trim().split(" ").slice(0, 2).join(" ");
  };


const formatPhoneForWhatsApp = (phone: string) => {
  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, "");
  
  // If phone starts with 0, replace it with country code 971
  if (digits.startsWith("0")) {
    return `971${digits.substring(1)}`;
  }
  
  // If already has country code (like 971), just return digits
  return digits;
};

// Usage




  const formattedClientItems = (clientItems ?? []).map((item) => {
    const remainingData = remainingDaysMap?.[item.id];
    const clientPhone = remainingData?.phone;

    return {
      ...item,
      name: getFirstTwoWords(item.name),
      touristVisaIssueDate: item.touristVisaIssueDate?.split("T")[0] ?? null,
      entryDateToUAE: item.entryDateToUAE?.split("T")[0] ?? null,
      remainingDaysToExit: remainingData?.remainingDays ?? null,
      allowedDuration: remainingData?.allowedDays ?? null,
      overstayDays: remainingData?.overstayDays ?? 0,
      overstayFine: remainingData?.overstayFine ?? 0,

      
      whatsAppLink: clientPhone
  ? `https://wa.me/${formatPhoneForWhatsApp(clientPhone)}`
  : null,

      
    };
  });

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPagingParams(new PagingParams(1, newSize));
    loadClientItems();
  };

  const handleGetNext = (page: number) => {
    setPagingParams(new PagingParams(page, pageSize));
    loadClientItems();
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setPagingParams(new PagingParams(1, pageSize));
    loadClientItems();
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchQuery("");
    setPagingParams(new PagingParams(1, pageSize));
    loadClientItems();
  };

  return (
  <div className="bg-none flex justify-center items-start px-4 py-4 w-full">
    <div className="w-full max-w-[1200px]">
      <div className="flex flex-col gap-4">
        {/* Filter Bar */}
        <TableFilterBar
          pagination={pagination}
          pageSize={pageSize}
          handlePageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          handleReset={handleReset}
        />

        {/* Table */}
        <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-sm">
          <TableClient
            data={formattedClientItems}
            rowsPerPageOptions={[10, 20, 50]}
            pageSize={pageSize}
            exceptColumns={[
              "fileEntities",
              "id",
              "createdBy",
              "updatedBy",
              "createDate",
              "updateDate",
              "bookingId",
              "followUpComments",
              "overstayDays",
              "whatsAppLink",
            ]}
            setSelectedId={setSelectedId}
            routeUrl={all_routes.clientDashboard}
            dialogFlags={{
              deleteDialog: "delete_client_dialog",
              visaEditDialog: "edit_visa_dialog",
            }}
            getViewId={(id) => getClientItem(id)}
            customCellRenderers={{
              overstayFine: (value: number) => (
                <span className="text-red-600 font-bold">{value}</span>
              ),
            }}
          />

          {/* Paginator */}
          <div className="flex justify-center pt-4">
            <Paginator handleGetNext={handleGetNext} pagination={pagination} />
          </div>
        </div>
      </div>

      {/* Modal for Editing Visa Info */}
      <div
        className="modal fade"
        id="edit_visa_dialog"
        tabIndex={-1}
        aria-labelledby="editVisaDialogLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header text-white" style={{ backgroundColor: "#cc3093" }}>
              <h5 className="modal-title">Edit Visa Dates</h5>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              <div className="flex flex-col gap-4">
                <div className="form-group w-full">
                  <label>Tourist Visa Issue Date</label>
                  <input
                    type="date"
                    className="form-control w-full"
                    value={formData.touristVisaIssueDate}
                    onChange={(e) => setFormData({ ...formData, touristVisaIssueDate: e.target.value })}
                  />
                </div>
                <div className="form-group w-full">
                  <label>Entry Date to UAE</label>
                  <input
                    type="date"
                    className="form-control w-full"
                    value={formData.entryDateToUAE}
                    onChange={(e) => setFormData({ ...formData, entryDateToUAE: e.target.value })}
                  />
                </div>
                <div className="form-group w-full">
                  <label>Follow-up Comments</label>
                  <textarea
                    className="form-control w-full"
                    rows={3}
                    placeholder="Add any relevant notes..."
                    value={formData.followUpComments}
                    onChange={(e) => setFormData({ ...formData, followUpComments: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer flex justify-end gap-2">
              <button className="btn btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#cc3093", borderColor: "#cc3093" }}
                onClick={async () => {
                  if (currentClientItem) {
                    await updateVisaDates(currentClientItem.id, {
                      touristVisaIssueDate: formData.touristVisaIssueDate || null,
                      entryDateToUAE: formData.entryDateToUAE || null,
                      followUpComments: formData.followUpComments,
                    });
                    await loadClientItems();
                  }
                }}
                data-bs-dismiss="modal"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

};

export default observer(ClientList);